// MediaRecorder wrapper for voice-to-notes.
//
// Distinct from useVoiceInput.ts (which wraps the browser Web Speech API
// for live dictation into text fields). This records an actual audio
// blob the advisor can speak a whole post-meeting memo into, which we
// then send to the server for Whisper transcription. MediaRecorder is
// supported in every modern browser including Safari 14.1+ — unlike the
// Web Speech API, which is flaky outside Chrome.
//
// Per-device only: nothing here touches storage; the blob lives in
// memory until the composer uploads it.

import { useCallback, useRef, useState } from "react";

export type RecorderState = "idle" | "recording" | "stopped" | "denied";

export interface UseAudioRecorder {
  state: RecorderState;
  /** Seconds elapsed in the current/last recording (updates ~1/s). */
  seconds: number;
  error: string | null;
  /** Resolves {ok:false, error} on unsupported/permission-denied so the
   *  caller can recover without reading stale hook state. */
  start: () => Promise<{ ok: boolean; error: string | null }>;
  /** Stops recording and resolves with the captured audio (null if none). */
  stop: () => Promise<Blob | null>;
  reset: () => void;
}

// Pick a mime type the browser can actually produce. Safari emits
// mp4/aac; Chrome/Firefox emit webm/opus. Whisper handles all of them.
function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = ["audio/webm", "audio/mp4", "audio/ogg"];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return undefined;
}

export function useAudioRecorder(): UseAudioRecorder {
  const [state, setState] = useState<RecorderState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Resolver for the in-flight stop() promise — settled by onstop so the
  // caller gets the blob without racing React state.
  const stopResolveRef = useRef<((b: Blob | null) => void) | null>(null);

  const stopTracks = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async (): Promise<{
    ok: boolean;
    error: string | null;
  }> => {
    setError(null);
    setSeconds(0);
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof MediaRecorder === "undefined"
    ) {
      const msg = "Recording isn't supported in this browser.";
      setError(msg);
      setState("denied");
      return { ok: false, error: msg };
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      const msg =
        "Microphone access was blocked. Allow it in your browser settings to record.";
      setError(msg);
      setState("denied");
      return { ok: false, error: msg };
    }
    streamRef.current = stream;
    chunksRef.current = [];
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined,
    );
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || "audio/webm";
      const out = new Blob(chunksRef.current, { type });
      stopTracks();
      setState("stopped");
      if (stopResolveRef.current) {
        stopResolveRef.current(out.size > 0 ? out : null);
        stopResolveRef.current = null;
      }
    };
    recorder.start();
    setState("recording");
    const startedAt = Date.now();
    tickRef.current = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return { ok: true, error: null };
  }, [stopTracks]);

  const stop = useCallback((): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return Promise.resolve(null);
    }
    return new Promise<Blob | null>((resolve) => {
      stopResolveRef.current = resolve;
      recorder.stop(); // fires onstop → resolves with the blob
    });
  }, []);

  const reset = useCallback(() => {
    stopTracks();
    recorderRef.current = null;
    chunksRef.current = [];
    setSeconds(0);
    setError(null);
    setState("idle");
  }, [stopTracks]);

  return { state, seconds, error, start, stop, reset };
}

/** Base64-encode a Blob's bytes (no data: prefix) for JSON upload. */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(binary);
}
